import {parseAndFormat} from "./commonUtils";
import { getCurrentYear } from "./dateUtils";

/**
 * Calculate Daily Interest using Two Values (bankLoanBasis * bankInterestRate / 360)
 */
export const dailyInt = (a: number, b: number) => {
    return parseAndFormat((a * b) / 360);
}

/**
 * Adds an arbitrary number of values together and returns the sum
 * rounded to two decimal places as a string.
 *
 * - Accepts both numbers and numeric strings.
 * - Ignores invalid (non-numeric) inputs.
 * - Returns the result as a string with exactly two decimal digits.
 *
 * @param {...(number|string)} args - List of values to sum.
 * @returns {string} The total sum rounded to two decimal places.
 *
 * @example
 * addNumbers(1, "2", 3.999, "4.999"); // "11.99"
 * addNumbers(5, "abc", 2);            // "7.00"
 */
export const addNumbers = (...args) => {
    const total = args.reduce((sum, num) => {
        const value = parseFloat(num);
        return isNaN(value) ? sum : sum + value;
    }, 0);
    return total.toFixed(2); // always 2 decimal places
};

/**
 * Calculate Interest Accrued YTD
 * @param a
 * @param b Anniversary Date
 */
export const intAccrued = (a: number, b: string) => {
    if(b === 'empty'){
        return 0;
    }

    const todayDate = new Date();
    const baseDate = new Date(b);

    // Check condition: if both years are Not same
    if (baseDate.getFullYear() !== todayDate.getFullYear()) {
        baseDate.setFullYear(todayDate.getFullYear() - 1);
    }

    // Calculate difference
    const diffMs = Number(todayDate) - Number(baseDate);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return (a * diffDays).toFixed(2);
}

/**
 * Calculate Actual Interest Amount
 */
export const actualIntAmt = (a: number, b: number) => {
    return parseAndFormat((a * b * 365) / 360);
}

/**
 * Generate Anniversary Date
 */
export const getAnniversaryDate = (date: any) => {
    const trancheIssueDate: Date = new Date(date);
    const today: Date = new Date();

    // Calculate how many full years have passed
    let yearsPassed = today.getFullYear() - trancheIssueDate.getFullYear();

    // Adjust if today's month/day is before the provided month/day
    const hasNotReachedAnniversary =
        today.getMonth() < trancheIssueDate.getMonth() ||
        (today.getMonth() === trancheIssueDate.getMonth() && today.getDate() < trancheIssueDate.getDate());

    if (hasNotReachedAnniversary) {
        yearsPassed -= 1;
    }

    // Calculate the "one-year-old" version of the date
    const oneYearOldDate = new Date(trancheIssueDate);
    oneYearOldDate.setFullYear(trancheIssueDate.getFullYear() + yearsPassed);

    // Display result
    if (yearsPassed < 1) {
        return 'empty'; // Not one year old yet
    } else {
        return oneYearOldDate.toISOString().split("T")[0]; // One-year-old date
    }
}

/**
 * Generate Bank Values under Case Detail Tab
 * @param yearPolicyBreakup
 * @param index
 * @param caseDetails
 * @param year
 */
export const getBankValues = (yearPolicyBreakup: any, index: number, caseDetails: any, year: number) => {
    let bankLoanBasis;
    const foundBankIR = caseDetails && caseDetails.tranche && caseDetails.tranche.rates && caseDetails.tranche.rates.find(item => item.policyYear === year);
    const bankIR = foundBankIR && foundBankIR.rate !== null ? foundBankIR.rate : 0;
    const bankLoanPremiumDue = parseAndFormat(yearPolicyBreakup.bankLoanPremiumDueUpdated ? yearPolicyBreakup.bankLoanPremiumDue : yearPolicyBreakup.bankPremium);
    const arrangementFee = parseAndFormat(caseDetails && caseDetails.tranche && caseDetails.tranche.bankDetails && caseDetails.tranche.bankDetails.length > 0 && caseDetails.tranche.bankDetails[0].totalArrangementFee);
    const bankInterestRate = yearPolicyBreakup && yearPolicyBreakup.bankInterestRateUpdated ? yearPolicyBreakup.bankInterestRate : bankIR;
    const anniversaryDate = getAnniversaryDate(caseDetails && caseDetails.tranche && caseDetails.tranche.issueDate);
    let actualInterestAmount;

    switch (index) {
        case 0:
            bankLoanBasis = yearPolicyBreakup && yearPolicyBreakup.bankLoanBasisUpdated ? yearPolicyBreakup.bankLoanBasis : addNumbers(bankLoanPremiumDue, arrangementFee);
            actualInterestAmount = yearPolicyBreakup.actualInterestAmount ? parseAndFormat(yearPolicyBreakup.actualInterestAmount) : actualIntAmt(bankLoanBasis, bankInterestRate);
            break;
        default:
            const foundEstimatedLB = caseDetails && caseDetails.policyBreakDowns.find(item => item.policyYear === (year - 1));

            const estimatedLoanBalance = foundEstimatedLB && ((foundEstimatedLB.bankLoanAmount === null || foundEstimatedLB.bankLoanAmount === 0) ?
                addNumbers(bankLoanBasis, actualInterestAmount) : foundEstimatedLB.bankLoanAmount);

            bankLoanBasis = yearPolicyBreakup && yearPolicyBreakup.bankLoanBasisUpdated ?
                            yearPolicyBreakup.bankLoanBasis : addNumbers(estimatedLoanBalance, bankLoanPremiumDue);

            actualInterestAmount = yearPolicyBreakup.actualInterestAmount ? parseAndFormat(yearPolicyBreakup.actualInterestAmount) : actualIntAmt(bankLoanBasis, bankInterestRate);
    }
    const dailyInterestAccrual = dailyInt(bankLoanBasis, bankInterestRate);
    const interestAccruedYTD = intAccrued(Number(dailyInterestAccrual), anniversaryDate);

    return {
        bLPD: bankLoanPremiumDue,
        bankIntRate: bankInterestRate,
        bankLB: bankLoanBasis,
        dailyIA: dailyInterestAccrual,
        intAccruedYTD: interestAccruedYTD,
        actualIA: actualInterestAmount,
        estimatedLoanBalance: yearPolicyBreakup.bankLoanAmount ? parseAndFormat(yearPolicyBreakup.bankLoanAmount) : addNumbers(bankLoanBasis, actualInterestAmount),
    };
};

/**
 * 
 * @param policyBreakDown 
 * @param currentYear 
 * @param yearCount 
 * @param adminClientAllocationsCurrentYear 
 * @returns 
 */
export const getCalculatedValues = (policyBreakDown) => {
    const cummulativeClientCont = 0;
    const currentYear = getCurrentYear();
    const yearCount = policyBreakDown.filter((p) => p.policyYear <= currentYear).length;
    const currentYearPolicyBreakup = (policyBreakDown && policyBreakDown.find((p) => p.policyYear === currentYear));
    const adminClientAllocationsCurrentYear = currentYearPolicyBreakup && currentYearPolicyBreakup.adminClientAllocations;
    const cumulativeAmountPaidToPolicy = getReducedValue("insurancePremiumActual", currentYear, policyBreakDown);
    const cumulativeClientContributionPaid = getCummulativeClientContibutionPay(policyBreakDown, currentYear, cummulativeClientCont);
    const cumulativeEmployerContributionPaid = getReducedValue("employerContributionReceived", currentYear, policyBreakDown);
    const cumulativeBankLoanPremiumPaid = getReducedValue("bankLoanPremiumDue", currentYear, policyBreakDown);
    const cumulativeGrowthRate = getReducedValueForRates("growthPercent", currentYear, policyBreakDown);
    const averageGrowthRate = yearCount > 0 ? Number(cumulativeGrowthRate / yearCount) : "";
    const cumulativeLoanRate = getReducedValueForRates("bankInterestRate", currentYear, policyBreakDown);
    const policyGrowthRate = calculateWeightedAverage(adminClientAllocationsCurrentYear, "indexCreditPercent", "indexAllocationPercent");
    const loanRateAverage = yearCount > 0 ? Number(cumulativeLoanRate / yearCount) : "";
    const growthDollarAmountEarned = adminClientAllocationsCurrentYear && adminClientAllocationsCurrentYear.reduce((acc, alloc) => acc + Number(alloc.indexCreditAmount || 0), 0);
    return {
        cumulativeAmountPaidToPolicy,
        cumulativeClientContributionPaid,
        cumulativeEmployerContributionPaid,
        cumulativeBankLoanPremiumPaid,
        averageGrowthRate,
        loanRateAverage,
        policyGrowthRate,
        growthDollarAmountEarned
    };
};

/**
* 
* @param arrayName 
* @param value 
* @param weight 
* @returns 
*/
export const calculateWeightedAverage = (arrayName: [], value: string, weight: string) => arrayName && (arrayName.reduce((sum, item) => sum + Number(item[value]) * Number(item[weight]), 0) /
    arrayName.reduce((sum, item) => sum + Number(item[weight]), 0));


/**
 * 
 * @param fieldName 
 * @param currentYear 
 * @param arrayName 
 * @returns 
 */
export const getReducedValue = (fieldName: string, currentYear: number, arrayName: any) => arrayName.reduce((acc, p) => {
    if (p.policyYear <= currentYear) {
        acc = acc + Number(p[fieldName]);
        return acc;
    }
    return acc;
}, 0);

/**
 * 
 * @param policyBreakdown 
 * @param currentYear 
 * @param cumulativeClientCont 
 * @returns 
 */
export const getCummulativeClientContibutionPay = (
    policyBreakdown: any[],
    currentYear: number,
    cumulativeClientCont: number = 0
) => {
    for (const p of policyBreakdown) {
        if (p.policyYear <= currentYear) {
            cumulativeClientCont += (p.payments || []).reduce(
                (acc: number, t: any) => acc + Number(t.amount || 0),
                0
            );
        }
    }
    return cumulativeClientCont;
};

/**
 * 
 * @param fieldName 
 * @param currentYear 
 * @param arrayName 
 * @returns 
 */
export const getReducedValueForRates = (fieldName: string, currentYear: number, arrayName: any) => arrayName.reduce((acc, p) => {
    if (p.policyYear <= currentYear) {
        const rate = Number(
            p[fieldName]
        );
        return acc + (isNaN(rate) ? 0 : rate);
    }
    return acc;
}, 0);